import type { ChangeEvent, KeyboardEvent } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type {
  CalculatorFormatValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { TextControl } from '@elfsight/widget-ui';
import {
  createReadableClass,
  formatNumberWithSeparatorOnEdit,
  parseNumberValue
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';

export interface NumberProps {
  id?: string;
  className?: string;
  value?: number;
  minValue?: number;
  maxValue?: number;
  initialValue?: string;
  decimalSeparator?: string;
  prefix?: string;
  suffix?: string;
  numberFormatter?: (value: number) => string;
  format?: CalculatorFormatValue;
  type?: string;
  label?: string;
  placeholder?: string;
  fieldLabelPosition?: 'outside' | 'inside';
  fieldLabelColor?: string;
  fieldInsideLabelColor?: string;
  fieldLabelFontStyle?: TextStyleValue;
  fieldInsideLabelFontStyle?: TextStyleValue;
  fieldColor?: string;
  fieldFontStyle?: TextStyleValue;
  fieldFillColor?: string;
  fieldActiveColor?: string;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldBorderType?: 'outline' | 'underline';
  fieldBorderRadius?: number;
  fieldPlaceholderFontStyle?: string;
  fieldPlaceholderFontWeight?: string;
  highlightError?: string;
  errorColor?: string;
  width?: number;
  presetWidth?: number;
  required?: boolean;
  fitContentWidth?: boolean;
  fieldID?: string;
  name?: string;
  placeholderColor?: string;
  variants?: any;
  underlineVariants?: any;
  _spacing?: boolean;
  valid?: boolean;
  fieldSpacing?: number;
  customVariant?: any;
  showLabel?: boolean;
  showPlaceholder?: boolean;
  onChange?: (
    value?: number,
    event?: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onFocus?: () => void;
  onBlur?: (value?: number) => void;
}

const MAX_NUMBER_LENGTH = 30;

export const NumberCalculatorComponent = forwardRef<
  TextControlBaseInstance,
  NumberProps
>(
  (
    {
      id,
      className,
      value,
      initialValue,
      minValue,
      maxValue,
      numberFormatter,
      format,
      decimalSeparator,
      type,
      label,
      showLabel,
      showPlaceholder = false,
      fieldInsideLabelColor,
      fieldLabelPosition,
      fieldLabelColor,
      fieldInsideLabelFontStyle,
      fieldLabelFontStyle,
      fieldBorderType,
      fieldBorderRadius,
      fieldColor,
      fieldFontStyle,
      fieldFillColor,
      fieldBorderWidth,
      fieldBorderColor,
      highlightError,
      errorColor = 'red',
      fieldActiveColor,
      placeholder,
      placeholderColor: derivedPlaceholderColor,
      onChange,
      onBlur,
      onFocus,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const baseValue = showPlaceholder ? '' : '0';

    const [inputValue, setInputValue] = useState<string>(
      String(value || initialValue || baseValue)
    );
    const [editingInput, setEditingInput] = useState<boolean>(false);

    const ref = useRef<TextControlBaseInstance>(null);
    const mergedRef = useMergedRef(ref, forwardingRef);

    const { suffix = '', prefix = '' } = format || {};

    const updateSelectionRange = () => {
      const target = ref.current?.inputRef.current;

      if (!target) {
        return;
      }

      requestAnimationFrame(() => {
        const start = prefix.length;
        const end = target.value.length - suffix.length;
        target.setSelectionRange(start, end);
      });
    };

    const handleCursorPositionChange = () => {
      const target = ref.current?.inputRef.current;

      if (!target) {
        return;
      }

      requestAnimationFrame(() => {
        const currentSelectionStart = target.selectionStart || 0;
        const currentSelectionEnd = target.selectionEnd || 0;
        const start = prefix.length;
        const end = target.value.length - suffix.length;
        if (
          currentSelectionStart < prefix.length ||
          currentSelectionEnd < prefix.length
        ) {
          target.setSelectionRange(start, start);
        }
        if (
          currentSelectionStart > prefix.length + inputValue.length ||
          currentSelectionEnd > prefix.length + inputValue.length
        ) {
          target.setSelectionRange(end, end);
        }
      });
    };

    const handleKeyDown = (
      event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      if (event.code.includes('Enter')) {
        event.preventDefault();
        (event.target as HTMLTextAreaElement | HTMLInputElement)?.blur();
        blurHandler();
      }

      handleCursorPositionChange();
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyA') {
        event.preventDefault();
        updateSelectionRange();
      }
    };

    const focusHandler = () => {
      onFocus?.();
      updateSelectionRange();
      setEditingInput(true);
    };

    const changeValueHandler = (
      changedValue: string,
      event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      const clearValue = removePrefixAndSuffix(changedValue, prefix, suffix);
      const formattedInputValue = formatNumberWithSeparatorOnEdit(
        clearValue,
        decimalSeparator
      );

      if (formattedInputValue.length === 0) {
        setInputValue('');
        onChange?.(undefined, event);
        updateSelectionRange();
        return;
      }
      if (maxValue && parseNumberValue(formattedInputValue) > maxValue) {
        // clamp max
        setInputValue(String(maxValue));
        onChange?.(maxValue, event);
        return;
      }
      if (minValue && parseNumberValue(formattedInputValue) < minValue) {
        // skip onChange for below min value
        setInputValue(formattedInputValue);
        return;
      }
      setInputValue(formattedInputValue);
      onChange?.(parseNumberValue(formattedInputValue), event);
    };

    const blurHandler = () => {
      if (inputValue === '' && showPlaceholder) {
        setInputValue('');
        onBlur?.();
        return;
      }

      const nextValue = parseNumberValue(inputValue);

      setEditingInput(false);

      if (isNaN(nextValue)) {
        setInputValue(baseValue);
        onBlur?.();
        return;
      }

      if (minValue && nextValue < minValue) {
        // clamp min
        setInputValue(
          formatNumberWithSeparatorOnEdit(String(minValue), decimalSeparator)
        );
        onBlur?.(minValue);
        return;
      }

      setInputValue(
        formatNumberWithSeparatorOnEdit(String(nextValue), decimalSeparator)
      );
      onBlur?.(nextValue);
    };

    useEffect(() => {
      const isDifferentValues = parseNumberValue(inputValue) !== value;
      if (typeof value === 'number' && isDifferentValues) {
        setInputValue(
          formatNumberWithSeparatorOnEdit(String(value), decimalSeparator)
        );
      }
      // oxlint-disable-next-line exhaustive-deps
    }, [value]);

    useEffect(() => {
      if (initialValue) {
        setInputValue(String(initialValue));
        onChange?.(parseNumberValue(initialValue));
      }
      // oxlint-disable-next-line exhaustive-deps
    }, [initialValue]);

    const insideLabel = fieldLabelPosition === 'inside';
    const labelColor = insideLabel ? fieldInsideLabelColor : fieldLabelColor;
    const labelFontStyle = insideLabel
      ? fieldInsideLabelFontStyle
      : fieldLabelFontStyle;
    const placeholderColor =
      derivedPlaceholderColor || getColorWithAlpha(String(fieldColor), 0.45);

    const withUnderline = fieldBorderType === 'underline';

    const maxBorderRadius = Math.round(
      insideLabel && type !== 'choice'
        ? ((fieldFontStyle?.fontSize || 1) * 3) / 2
        : ((fieldFontStyle?.fontSize || 1) * 7) / 6
    );
    const borderRadius = Math.min(fieldBorderRadius || 1, maxBorderRadius);

    const ariaLabel = typeof label === 'string' ? label : undefined;

    return (
      <TextControl
        {...forwardingProps}
        id={id}
        className={createReadableClass(
          {
            component: 'fields',
            element: 'number'
          },
          [className]
        )}
        inputMode="decimal"
        ref={mergedRef}
        initialValue={initialValue}
        value={
          editingInput
            ? prefix + inputValue + suffix
            : (numberFormatter?.(parseNumberValue(inputValue)) ?? inputValue)
        }
        maxLength={MAX_NUMBER_LENGTH + suffix.length + prefix.length}
        onClick={handleCursorPositionChange}
        onKeyDown={handleKeyDown}
        onDoubleClick={updateSelectionRange}
        onFocus={focusHandler}
        onBlur={blurHandler}
        onValueChange={changeValueHandler}
        placeholder={placeholder}
        placeholderColor={placeholderColor}
        color={fieldColor}
        font={fieldFontStyle}
        ariaLabel={ariaLabel}
        label={showLabel && insideLabel ? label : null}
        labelColor={labelColor}
        labelFont={labelFontStyle}
        background={fieldFillColor}
        borderType={fieldBorderType}
        borderWidth={fieldBorderWidth}
        borderRadius={borderRadius}
        borderColor={fieldBorderColor}
        customVariant={highlightError ? 'error' : undefined}
        variants={
          !withUnderline
            ? {
                focused: {
                  borderColor: fieldActiveColor
                },
                error: {
                  borderColor: errorColor
                }
              }
            : undefined
        }
        underlineVariants={
          withUnderline
            ? {
                focused: {
                  borderColor: fieldActiveColor
                },
                error: {
                  borderColor: errorColor
                }
              }
            : undefined
        }
      />
    );
  }
);

const removePrefixAndSuffix = (
  value: string,
  prefix: string,
  suffix: string
): string => {
  const escapedSuffix = suffix.replace(/([\\()[\]~$*?+.])/g, '\\$1');

  return value
    .replace(prefix, '')
    .replace(new RegExp(`${escapedSuffix}(?!.*${escapedSuffix})`), '');
};
